// Sharing and exporting audio with its lesson text.
//
// One implementation for both the lesson audio and a student's recording, so
// the two behave identically. Everything here was learned the hard way:
//
//  - Browsers differ on what they accept. iOS Safari commonly refuses a file
//    bundled with text while sharing the same file alone without complaint, so
//    the share walks a chain and gives up the audio last.
//  - navigator.share() must be called while the user gesture is still live.
//    Nothing in here may await before it — in particular the caller must pass
//    a Blob that is already in memory, never a URL to fetch.
//  - When the text cannot travel with the audio it is put on the clipboard so
//    it is one paste away.

// The Web Share allow-list is matched against bare MIME types, not the
// parameterised form MediaRecorder reports ("audio/mp4;codecs=mp4a.40.2").
export function bareType(type, fallback = 'audio/mp4') {
  return (type || '').split(';')[0].trim() || fallback
}

export function extensionFor(type) {
  const t = bareType(type, '')
  if (t.includes('mpeg') || t.includes('mp3')) return 'mp3'
  if (t.includes('mp4') || t.includes('m4a') || t.includes('aac')) return 'm4a'
  if (t.includes('ogg')) return 'ogg'
  if (t.includes('wav')) return 'wav'
  return 'webm'
}

// A filename a teacher can read in a chat or a folder listing. Keeps Ethiopic
// script, drops anything a filesystem would object to.
export function audioFileName(label, type, fallback = 'Kidase recording') {
  const clean = (label || '')
    .replace(/[^a-zA-Z0-9ሀ-፿ ]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
  return `${clean || fallback}.${extensionFor(type)}`
}

export function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

/**
 * Share audio together with its text, falling back until something lands.
 * Must be called directly from a user gesture with a Blob already in hand.
 *
 * Returns { outcome, copied } where outcome is one of:
 *   'shared'     audio and text went together
 *   'audio-only' the target took the file but refused the text
 *   'text-only'  no audio could be sent
 *   'downloaded' no share sheet; the file was saved instead
 *   'cancelled'  the user dismissed the sheet
 */
export async function shareAudioWithText({ blob, fileName, title, text }) {
  const file = blob
    ? new File([blob], fileName || audioFileName(title, blob.type), { type: bareType(blob.type) })
    : null

  // Richest first; the bare file outranks text because losing the audio is
  // the worse outcome — the text can go on the clipboard, the audio cannot.
  const candidates = []
  if (file) {
    candidates.push({ title, text, files: [file] })
    candidates.push({ title, files: [file] })
    candidates.push({ files: [file] })
  }
  if (text) candidates.push({ title, text })

  // Fired, not awaited: awaiting here would spend the gesture share() needs.
  let copyPromise = null
  try {
    copyPromise = text && navigator.clipboard ? navigator.clipboard.writeText(text) : null
  } catch { /* clipboard unavailable */ }
  const wasCopied = async () => {
    if (!copyPromise) return false
    try { await copyPromise; return true } catch { return false }
  }

  if (navigator.share) {
    for (const data of candidates) {
      if (navigator.canShare && !navigator.canShare(data)) continue
      try {
        await navigator.share(data)
        if (!data.files) return { outcome: 'text-only', copied: await wasCopied() }
        if (!data.text) return { outcome: 'audio-only', copied: await wasCopied() }
        return { outcome: 'shared', copied: false }
      } catch (err) {
        // A dismissed sheet is a decision, not a failure to route around.
        if (err.name === 'AbortError') return { outcome: 'cancelled', copied: false }
      }
    }
  }

  if (blob) {
    downloadBlob(blob, file.name)
    return { outcome: 'downloaded', copied: await wasCopied() }
  }
  return { outcome: 'text-only', copied: await wasCopied() }
}

// One wording for every caller, so the lesson and the recording explain
// themselves the same way.
// navigator.share() resolving means the share sheet accepted the payload. It
// does NOT mean anything was delivered: the browser never learns which app was
// chosen, whether the user cancelled inside it, or whether the attachment
// survived. So none of these say "sent" — they say what the app did and ask
// the user to confirm the result, which is the only thing that is knowable.
export function shareNoteFor({ outcome, copied }) {
  switch (outcome) {
    case 'audio-only':
      return copied
        ? 'Passed to the app you chose — check the chat to confirm the audio arrived. The lesson text was copied, ready to paste.'
        : 'Passed to the app you chose — check the chat to confirm the audio arrived.'
    case 'text-only':
      return 'Only the text was passed on — the audio was NOT included. Use Save to download the recording and attach it.'
    case 'shared':
      return 'Passed to the app you chose — check the chat to confirm the audio arrived.'
    case 'downloaded':
      return copied
        ? 'Saved to your downloads and the text copied — drag the file into Telegram and paste the text.'
        : 'Saved to your downloads — attach it in Telegram. Sharing straight to an app works from a phone.'
    default:
      return ''
  }
}
